import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  serverTimestamp, query, where, orderBy, limit, startAfter, runTransaction, writeBatch,
  QueryDocumentSnapshot, DocumentData
} from 'firebase/firestore';
import { db } from './firebase';
import { Store, StoreInventory, StoreMinQuantity, UsageRecord, UsageSession, EquipmentItemType } from '../types';
import { logAuditEvent } from './auditService';

type SessionsSnap = { docs: Array<QueryDocumentSnapshot<DocumentData>> };

export interface UsageSessionsPage {
  sessions: UsageSession[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

async function assertLegacyStores(...storeIds: (string | undefined)[]): Promise<void> {
  for (const storeId of storeIds.filter(Boolean) as string[]) {
    if ((await getDoc(doc(db, 'stores', storeId))).data()?.inventoryVersion === 2) {
      throw new Error('BATCH_MANAGED_STORE');
    }
  }
}

export async function getStores(): Promise<Store[]> {
  const snap = await getDocs(collection(db, 'stores'));
  const stores = snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      inventoryVersion: data.inventoryVersion ?? 1,
      caretakerIds: data.caretakerIds ?? [],
      name: data.name,
      weight: data.weight ?? 0,
      createdAt: data.createdAt?.toDate?.() ?? new Date(),
    };
  });
  return stores.sort((a, b) => a.weight - b.weight);
}

export async function createStore(name: string, weight: number = 0): Promise<string> {
  const ref = await addDoc(collection(db, 'stores'), {
    name,
    weight,
    createdAt: serverTimestamp(),
  });
  logAuditEvent({ entityType: 'store', entityId: ref.id, action: 'create', details: name }).catch(() => {});
  return ref.id;
}

export async function updateStore(id: string, name: string, weight: number = 0): Promise<void> {
  await updateDoc(doc(db, 'stores', id), { name, weight });
  logAuditEvent({ entityType: 'store', entityId: id, action: 'edit', details: name }).catch(() => {});
}

export async function deleteStore(id: string): Promise<void> {
  await deleteDoc(doc(db, 'stores', id));
  logAuditEvent({ entityType: 'store', entityId: id, action: 'delete' }).catch(() => {});
}

export async function getStoreInventory(storeId: string): Promise<StoreInventory[]> {
  const q = query(collection(db, 'store_inventory'), where('storeId', '==', storeId));
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      storeId: data.storeId,
      equipmentId: data.equipmentId,
      quantity: data.quantity,
      updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
    };
  });
}

export async function getAllStoreInventory(): Promise<StoreInventory[]> {
  const snap = await getDocs(collection(db, 'store_inventory'));
  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      storeId: data.storeId,
      equipmentId: data.equipmentId,
      quantity: data.quantity,
      updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
    };
  });
}

export async function getStoreMinQuantities(storeId: string): Promise<StoreMinQuantity[]> {
  const q = query(collection(db, 'store_min_quantities'), where('storeId', '==', storeId));
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      storeId: data.storeId,
      equipmentId: data.equipmentId,
      minQuantity: data.minQuantity ?? 0,
    };
  });
}

/**
 * Saves store-specific min quantity overrides.
 * Entries whose value matches the catalogue default are deleted from the database.
 * @param storeId - the store ID
 * @param limits - map of equipmentId -> desired minQuantity
 * @param catalogueDefaults - map of equipmentId -> catalogue default minQuantity
 */
export async function saveStoreMinQuantities(
  storeId: string,
  limits: Record<string, number>,
  catalogueDefaults: Record<string, number>,
): Promise<void> {
  const existing = await getStoreMinQuantities(storeId);
  const existingMap: Record<string, string> = {}; // equipmentId -> docId
  for (const e of existing) {
    existingMap[e.equipmentId] = e.id;
  }

  const batch = writeBatch(db);

  for (const [equipmentId, minQuantity] of Object.entries(limits)) {
    const catalogueDefault = catalogueDefaults[equipmentId] ?? 0;
    const existingDocId = existingMap[equipmentId];

    if (minQuantity === catalogueDefault) {
      // Same as catalogue default — remove store-specific override if it exists
      if (existingDocId) {
        batch.delete(doc(db, 'store_min_quantities', existingDocId));
      }
    } else if (existingDocId) {
      batch.update(doc(db, 'store_min_quantities', existingDocId), { minQuantity });
    } else {
      const newRef = doc(collection(db, 'store_min_quantities'));
      batch.set(newRef, { storeId, equipmentId, minQuantity });
    }
  }

  await batch.commit();
}

async function findStockDocId(storeId: string, equipmentId: string): Promise<string | null> {
  const q = query(
    collection(db, 'store_inventory'),
    where('storeId', '==', storeId),
    where('equipmentId', '==', equipmentId)
  );
  const snap = await getDocs(q);
  return snap.empty ? null : snap.docs[0].id;
}

export async function createUsageRecord(record: Omit<UsageRecord, 'id' | 'createdAt'>): Promise<void> {
  await assertLegacyStores(record.fromStoreId, record.toStoreId);
  // Resolve stock document IDs before entering the transaction to allow
  // transaction.get() calls on specific document references inside.
  let fromStockId: string | null = null;
  let toStockId: string | null = null;

  if ((record.type === 'take' || record.type === 'transfer') && record.fromStoreId) {
    fromStockId = await findStockDocId(record.fromStoreId, record.equipmentId);
  }
  if ((record.type === 'add' || record.type === 'transfer') && record.toStoreId) {
    toStockId = await findStockDocId(record.toStoreId, record.equipmentId);
  }

  await runTransaction(db, async (transaction) => {
    const usageRef = doc(collection(db, 'usage'));
    const equipment = await transaction.get(doc(db, 'inventory', record.equipmentId));
    if (!equipment.exists()) throw new Error('EQUIPMENT_NOT_FOUND');
    const reusableUse = record.type === 'take' && equipment.data().itemType === 'reusable';
    if (reusableUse && record.quantity !== 1) throw new Error('INVALID_REUSABLE_USE');
    let previousFromStoreQuantity: number | null = null;
    let previousToStoreQuantity: number | null = null;

    if (record.type === 'take' && record.fromStoreId) {
      if (!fromStockId) throw new Error('No stock found for this equipment in the selected store.');
      const stockRef = doc(db, 'store_inventory', fromStockId);
      const stockSnap = await transaction.get(stockRef);
      const currentQty = (stockSnap.data()?.quantity ?? 0) as number;
      if (currentQty < record.quantity) throw new Error('Insufficient stock.');
      previousFromStoreQuantity = currentQty;
      if (!reusableUse) transaction.update(stockRef, { quantity: currentQty - record.quantity, updatedAt: serverTimestamp() });
    } else if (record.type === 'add' && record.toStoreId) {
      if (!toStockId) {
        const newStockRef = doc(collection(db, 'store_inventory'));
        previousToStoreQuantity = 0;
        transaction.set(newStockRef, {
          storeId: record.toStoreId,
          equipmentId: record.equipmentId,
          quantity: record.quantity,
          updatedAt: serverTimestamp(),
        });
      } else {
        const stockRef = doc(db, 'store_inventory', toStockId);
        const stockSnap = await transaction.get(stockRef);
        const currentQty = (stockSnap.data()?.quantity ?? 0) as number;
        previousToStoreQuantity = currentQty;
        transaction.update(stockRef, { quantity: currentQty + record.quantity, updatedAt: serverTimestamp() });
      }
    } else if (record.type === 'transfer' && record.fromStoreId && record.toStoreId) {
      if (!fromStockId) throw new Error('No stock found in source store.');
      const fromRef = doc(db, 'store_inventory', fromStockId);
      const fromSnap = await transaction.get(fromRef);
      const fromQty = (fromSnap.data()?.quantity ?? 0) as number;
      if (fromQty < record.quantity) throw new Error('Insufficient stock in source store.');
      previousFromStoreQuantity = fromQty;
      transaction.update(fromRef, { quantity: fromQty - record.quantity, updatedAt: serverTimestamp() });

      if (!toStockId) {
        const newRef = doc(collection(db, 'store_inventory'));
        previousToStoreQuantity = 0;
        transaction.set(newRef, {
          storeId: record.toStoreId,
          equipmentId: record.equipmentId,
          quantity: record.quantity,
          updatedAt: serverTimestamp(),
        });
      } else {
        const toRef = doc(db, 'store_inventory', toStockId);
        const toSnap = await transaction.get(toRef);
        const toQty = (toSnap.data()?.quantity ?? 0) as number;
        previousToStoreQuantity = toQty;
        transaction.update(toRef, { quantity: toQty + record.quantity, updatedAt: serverTimestamp() });
      }
    }

    transaction.set(usageRef, {
      type: record.type,
      operationType: reusableUse ? 'use' : record.type,
      equipmentId: record.equipmentId,
      quantity: record.quantity,
      fromStoreId: record.fromStoreId ?? null,
      toStoreId: record.toStoreId ?? null,
      previousFromStoreQuantity,
      previousToStoreQuantity,
      userId: record.userId,
      note: record.note ?? '',
      createdAt: serverTimestamp(),
    });
  });
}

export async function getUsageRecords(): Promise<UsageRecord[]> {
  const snap = await getDocs(collection(db, 'usage'));
  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      sessionId: data.sessionId ?? undefined,
      operationType: data.operationType,
      type: data.type,
      equipmentId: data.equipmentId,
      quantity: data.quantity,
      fromStoreId: data.fromStoreId ?? undefined,
      toStoreId: data.toStoreId ?? undefined,
      previousFromStoreQuantity: data.previousFromStoreQuantity != null ? data.previousFromStoreQuantity : undefined,
      previousToStoreQuantity: data.previousToStoreQuantity != null ? data.previousToStoreQuantity : undefined,
      userId: data.userId,
      createdAt: data.createdAt?.toDate?.() ?? new Date(),
      note: data.note ?? undefined,
      actionNumber: data.actionNumber ?? undefined,
      description: data.description ?? undefined,
      actionDate: data.actionDate?.toDate?.() ?? undefined,
    };
  });
}

function buildUsageSessions(
  sessionsSnap: SessionsSnap,
  allRecords: UsageRecord[],
): UsageSession[] {
  const recordsBySession: Record<string, UsageRecord[]> = {};
  const legacyRecords: UsageRecord[] = [];

  for (const record of allRecords) {
    if (record.sessionId) {
      if (!recordsBySession[record.sessionId]) {
        recordsBySession[record.sessionId] = [];
      }
      recordsBySession[record.sessionId].push(record);
    } else {
      legacyRecords.push(record);
    }
  }

  const sessions: UsageSession[] = sessionsSnap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      type: data.type as UsageSession['type'],
      operationType: data.operationType as UsageSession['operationType'],
      fromStoreId: (data.fromStoreId as string) ?? undefined,
      toStoreId: (data.toStoreId as string) ?? undefined,
      userId: data.userId as string,
      actionNumber: (data.actionNumber as string) ?? undefined,
      description: (data.description as string) ?? undefined,
      actionDate: (data.actionDate as { toDate?: () => Date })?.toDate?.() ?? undefined,
      createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
      items: recordsBySession[d.id] ?? [],
    };
  });

  for (const record of legacyRecords) {
    sessions.push({
      id: record.id,
      type: record.type,
      operationType: record.operationType,
      fromStoreId: record.fromStoreId,
      toStoreId: record.toStoreId,
      userId: record.userId,
      actionNumber: record.actionNumber,
      description: record.description ?? record.note,
      actionDate: record.actionDate,
      createdAt: record.createdAt,
      items: [record],
    });
  }

  return sessions.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

function mapUsageRecord(d: QueryDocumentSnapshot<DocumentData>): UsageRecord {
  const data = d.data();
  return {
    id: d.id,
    sessionId: (data.sessionId as string) ?? undefined,
    type: data.type as UsageRecord['type'],
    operationType: data.operationType as UsageRecord['operationType'],
    equipmentId: data.equipmentId as string,
    quantity: data.quantity as number,
    fromStoreId: (data.fromStoreId as string) ?? undefined,
    toStoreId: (data.toStoreId as string) ?? undefined,
    previousFromStoreQuantity: data.previousFromStoreQuantity != null ? (data.previousFromStoreQuantity as number) : undefined,
    previousToStoreQuantity: data.previousToStoreQuantity != null ? (data.previousToStoreQuantity as number) : undefined,
    userId: data.userId as string,
    createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
    note: (data.note as string) ?? undefined,
    actionNumber: (data.actionNumber as string) ?? undefined,
    description: (data.description as string) ?? undefined,
    actionDate: (data.actionDate as { toDate?: () => Date })?.toDate?.() ?? undefined,
  };
}

async function getUsageRecordsBySessionIds(sessionIds: string[]): Promise<UsageRecord[]> {
  const recordDocs: QueryDocumentSnapshot<DocumentData>[] = [];
  for (let i = 0; i < sessionIds.length; i += 10) {
    const chunk = sessionIds.slice(i, i + 10);
    const recordsSnap = await getDocs(
      query(collection(db, 'usage'), where('sessionId', 'in', chunk))
    );
    recordDocs.push(...recordsSnap.docs);
  }
  return recordDocs.map(mapUsageRecord);
}

export async function getUsageSessionsPage(
  userId: string,
  pageSize: number = 25,
  cursor?: QueryDocumentSnapshot<DocumentData> | null,
): Promise<UsageSessionsPage> {
  if (!userId) {
    return { sessions: [], cursor: null, hasMore: false };
  }

  const constraints = [
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    ...(cursor ? [startAfter(cursor)] : []),
    limit(pageSize),
  ];
  const sessionsSnap = await getDocs(query(collection(db, 'usage_sessions'), ...constraints));
  const records = await getUsageRecordsBySessionIds(
    sessionsSnap.docs.map((d: QueryDocumentSnapshot<DocumentData>) => d.id)
  );

  return {
    sessions: buildUsageSessions({ docs: sessionsSnap.docs }, records),
    cursor: sessionsSnap.docs[sessionsSnap.docs.length - 1] ?? null,
    hasMore: sessionsSnap.docs.length === pageSize,
  };
}

export async function getUsageSessions(): Promise<UsageSession[]> {
  const [sessionsSnap, recordsSnap] = await Promise.all([
    getDocs(collection(db, 'usage_sessions')),
    getDocs(collection(db, 'usage')),
  ]);
  const allRecords = recordsSnap.docs.map(mapUsageRecord);
  return buildUsageSessions(sessionsSnap, allRecords);
}

export async function getUsageSessionsByUser(userId: string): Promise<UsageSession[]> {
  const [sessionsSnap, recordsSnap] = await Promise.all([
    getDocs(query(collection(db, 'usage_sessions'), where('userId', '==', userId))),
    getDocs(query(collection(db, 'usage'), where('userId', '==', userId))),
  ]);
  const allRecords = recordsSnap.docs.map(mapUsageRecord);
  return buildUsageSessions(sessionsSnap, allRecords);
}

export async function getUsageSessionsByActionNumber(actionNumber: string): Promise<UsageSession[]> {
  const sessionsSnap = await getDocs(
    query(collection(db, 'usage_sessions'), where('actionNumber', '==', actionNumber))
  );
  if (sessionsSnap.empty) return [];

  const sessionIds = sessionsSnap.docs.map(d => d.id);
  const recordDocs: QueryDocumentSnapshot<DocumentData>[] = [];
  for (let i = 0; i < sessionIds.length; i += 10) {
    const chunk = sessionIds.slice(i, i + 10);
    const recordsSnap = await getDocs(
      query(collection(db, 'usage'), where('sessionId', 'in', chunk))
    );
    recordDocs.push(...recordsSnap.docs);
  }

  const allRecords = recordDocs.map(mapUsageRecord);
  return buildUsageSessions(sessionsSnap, allRecords);
}

export async function getUsageSessionsByStore(storeId: string): Promise<UsageSession[]> {
  const [fromSessionsSnap, toSessionsSnap, fromRecordsSnap, toRecordsSnap] = await Promise.all([
    getDocs(query(collection(db, 'usage_sessions'), where('fromStoreId', '==', storeId))),
    getDocs(query(collection(db, 'usage_sessions'), where('toStoreId', '==', storeId))),
    getDocs(query(collection(db, 'usage'), where('fromStoreId', '==', storeId))),
    getDocs(query(collection(db, 'usage'), where('toStoreId', '==', storeId))),
  ]);

  // Deduplicate sessions (a session could appear in both from/to queries for transfers)
  const sessionMap = new Map<string, (typeof fromSessionsSnap.docs)[0]>();
  for (const d of [...fromSessionsSnap.docs, ...toSessionsSnap.docs]) {
    sessionMap.set(d.id, d);
  }
  const mergedSessionsSnap = { docs: Array.from(sessionMap.values()) };

  // Deduplicate records
  const recordMap = new Map<string, (typeof fromRecordsSnap.docs)[0]>();
  for (const d of [...fromRecordsSnap.docs, ...toRecordsSnap.docs]) {
    recordMap.set(d.id, d);
  }
  const allRecords = Array.from(recordMap.values()).map(mapUsageRecord);

  return buildUsageSessions(mergedSessionsSnap, allRecords);
}

export async function createActionUsageRecords(
  items: { equipmentId: string; quantity: number; itemType?: EquipmentItemType }[],
  meta: {
    type: 'take' | 'add' | 'transfer' | 'stocktaking';
    actionNumber?: string;
    description?: string;
    actionDate: Date;
    userId: string;
    fromStoreId?: string;
    toStoreId?: string;
  }
): Promise<void> {
  if (items.length === 0) return;
  await assertLegacyStores(meta.fromStoreId, meta.toStoreId);

  // Pre-resolve existing stock document IDs outside the transaction
  const fromStockIdMap: Record<string, string | null> = {};
  const toStockIdMap: Record<string, string | null> = {};

  for (const item of items) {
    if ((meta.type === 'take' || meta.type === 'transfer') && meta.fromStoreId) {
      fromStockIdMap[item.equipmentId] = await findStockDocId(meta.fromStoreId, item.equipmentId);
    }
    if ((meta.type === 'add' || meta.type === 'transfer' || meta.type === 'stocktaking') && meta.toStoreId) {
      toStockIdMap[item.equipmentId] = await findStockDocId(meta.toStoreId, item.equipmentId);
    }
  }

  // Create a session document to group all items from this wizard submission
  const sessionRef = doc(collection(db, 'usage_sessions'));

  await runTransaction(db, async (transaction) => {
    // --- All reads must come before any writes ---

    // Read existing stock snapshots for items that have an existing doc.
    const fromSnapMap: Record<string, number> = {};
    const toSnapMap: Record<string, number> = {};
    const reusableIds = new Set<string>();

    for (const item of items) {
      const equipment = await transaction.get(doc(db, 'inventory', item.equipmentId));
      if (!equipment.exists()) throw new Error('EQUIPMENT_NOT_FOUND');
      const itemType = equipment.data().itemType === 'reusable' ? 'reusable' : 'disposable';
      if (item.itemType !== undefined && item.itemType !== itemType) throw new Error('ITEM_TYPE_CHANGED');
      if (meta.type === 'take' && itemType === 'reusable') {
        if (item.quantity !== 1) throw new Error('INVALID_REUSABLE_USE');
        reusableIds.add(item.equipmentId);
      }
      if ((meta.type === 'take' || meta.type === 'transfer') && meta.fromStoreId) {
        const fromStockId = fromStockIdMap[item.equipmentId];
        if (fromStockId) {
          const stockRef = doc(db, 'store_inventory', fromStockId);
          const stockSnap = await transaction.get(stockRef);
          fromSnapMap[item.equipmentId] = (stockSnap.data()?.quantity ?? 0) as number;
        }
      }

      if ((meta.type === 'add' || meta.type === 'transfer' || meta.type === 'stocktaking') && meta.toStoreId) {
        const toStockId = toStockIdMap[item.equipmentId];
        if (toStockId) {
          const stockRef = doc(db, 'store_inventory', toStockId);
          const stockSnap = await transaction.get(stockRef);
          toSnapMap[item.equipmentId] = (stockSnap.data()?.quantity ?? 0) as number;
        }
      }
    }

    // Validate quantities before writing anything.
    for (const item of items) {
      if (reusableIds.has(item.equipmentId) && (fromSnapMap[item.equipmentId] ?? 0) < 1)
        throw new Error('INSUFFICIENT_STOCK');
      if (meta.type === 'transfer' && meta.fromStoreId) {
        const fromStockId = fromStockIdMap[item.equipmentId];
        if (!fromStockId) throw new Error('No stock found in source store.');
        const currentQty = fromSnapMap[item.equipmentId] ?? 0;
        if (currentQty < item.quantity) throw new Error('Insufficient stock in source store.');
      }
    }

    // --- All writes ---

    // Write the session document
    transaction.set(sessionRef, {
      type: meta.type,
      operationType: reusableIds.size === items.length ? 'use' : reusableIds.size ? 'mixed' : meta.type,
      fromStoreId: meta.fromStoreId ?? null,
      toStoreId: meta.toStoreId ?? null,
      userId: meta.userId,
      actionNumber: meta.actionNumber ?? '',
      description: meta.description ?? '',
      actionDate: meta.actionDate,
      createdAt: serverTimestamp(),
    });

    for (const item of items) {
      // Deduct from source store (take / transfer)
      if ((meta.type === 'take' || meta.type === 'transfer') && meta.fromStoreId && !reusableIds.has(item.equipmentId)) {
        const fromStockId = fromStockIdMap[item.equipmentId];
        if (!fromStockId) {
          if (meta.type !== 'take') throw new Error('No stock found in source store.');
          // No existing stock record for a 'take' — create one with a negative
          // quantity to reflect that more was taken than recorded in the system.
          const newRef = doc(collection(db, 'store_inventory'));
          transaction.set(newRef, {
            storeId: meta.fromStoreId,
            equipmentId: item.equipmentId,
            quantity: -item.quantity,
            updatedAt: serverTimestamp(),
          });
        } else {
          const stockRef = doc(db, 'store_inventory', fromStockId);
          const currentQty = fromSnapMap[item.equipmentId] ?? 0;
          transaction.update(stockRef, {
            quantity: currentQty - item.quantity,
            updatedAt: serverTimestamp(),
          });
        }
      }

      // Add to destination store (add / transfer)
      if ((meta.type === 'add' || meta.type === 'transfer') && meta.toStoreId) {
        const toStockId = toStockIdMap[item.equipmentId];
        if (!toStockId) {
          const newRef = doc(collection(db, 'store_inventory'));
          transaction.set(newRef, {
            storeId: meta.toStoreId,
            equipmentId: item.equipmentId,
            quantity: item.quantity,
            updatedAt: serverTimestamp(),
          });
        } else {
          const stockRef = doc(db, 'store_inventory', toStockId);
          const currentQty = toSnapMap[item.equipmentId] ?? 0;
          transaction.update(stockRef, {
            quantity: currentQty + item.quantity,
            updatedAt: serverTimestamp(),
          });
        }
      }

      // Set absolute quantity for stocktaking (verifies and corrects store inventory)
      if (meta.type === 'stocktaking' && meta.toStoreId) {
        const toStockId = toStockIdMap[item.equipmentId];
        if (!toStockId) {
          const newRef = doc(collection(db, 'store_inventory'));
          transaction.set(newRef, {
            storeId: meta.toStoreId,
            equipmentId: item.equipmentId,
            quantity: item.quantity,
            updatedAt: serverTimestamp(),
          });
        } else {
          const stockRef = doc(db, 'store_inventory', toStockId);
          transaction.update(stockRef, {
            quantity: item.quantity,
            updatedAt: serverTimestamp(),
          });
        }
      }

      const usageRef = doc(collection(db, 'usage'));
      const previousFromStoreQuantity =
        (meta.type === 'take' || meta.type === 'transfer') && meta.fromStoreId
          ? (fromSnapMap[item.equipmentId] ?? 0)
          : undefined;
      const previousToStoreQuantity =
        (meta.type === 'add' || meta.type === 'transfer' || meta.type === 'stocktaking') && meta.toStoreId
          ? (toSnapMap[item.equipmentId] ?? 0)
          : undefined;
      transaction.set(usageRef, {
        sessionId: sessionRef.id,
        type: meta.type,
        operationType: reusableIds.has(item.equipmentId) ? 'use' : meta.type,
        equipmentId: item.equipmentId,
        quantity: item.quantity,
        fromStoreId: meta.fromStoreId ?? null,
        toStoreId: meta.toStoreId ?? null,
        previousFromStoreQuantity: previousFromStoreQuantity ?? null,
        previousToStoreQuantity: previousToStoreQuantity ?? null,
        userId: meta.userId,
        note: '',
        actionNumber: meta.actionNumber ?? '',
        description: meta.description ?? '',
        actionDate: meta.actionDate,
        createdAt: serverTimestamp(),
      });
    }
  });

  logAuditEvent({
    entityType: 'usage',
    entityId: sessionRef.id,
    action: 'create',
    details: `${meta.type}${meta.actionNumber ? ` #${meta.actionNumber}` : ''} — ${items.length} item(s)`,
  }).catch(() => {});
}
